import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@antimatterhq/ui/components/button';
import Table from '@antimatterhq/ui/components/table';
import { core_v1_Rule } from '@antimatterhq/asl-api-ts-v1';
import { useTableColumnFiltering } from '@antimatterhq/ui/hooks/table/useTableColumnFiltering';
import { useTableColumns } from '@antimatterhq/ui/hooks/table/useTableColumns';
import { useTableColumnSorting } from '@antimatterhq/ui/hooks/table/useTableColumnSorting';
import { useTableRowFuzzyFiltering } from '@antimatterhq/ui/hooks/table/useTableRowFuzzyFiltering';
import { Input } from '@antimatterhq/ui/components/input';
import { Icon } from '@antimatterhq/ui/components/icon';
import { Badge } from '@antimatterhq/ui/components/badge';
import { useVirtuosoTableRows } from '@antimatterhq/ui/hooks/table/useVirtuosoTableRows';
import Spacer from '@antimatterhq/ui/components/spacer';
import { DetectionRuleTableData } from '@/components/table/DetectionRulesTable/DetectionRulesTableData';
import { cn } from '@/lib/utils';
import { detectionRulesTableColumnDefinitions } from '@/components/table/DetectionRulesTable/detectionTulesTableColumnDefinitions';
import { DeleteDetectionRulesDialog } from '@/components/detectionRules/deleteDetectionRulesDialog/DeleteDetectionRulesDialog';
import { useGetRulesInfinite } from '@/hooks/core/useGetRulesInfinite';

const SeverityBadge = ({ severity }: { severity: string }) => {
  const variants: Record<string, 'danger' | 'warning' | 'success' | 'default'> =
    {
      critical: 'danger',
      high: 'danger',
      medium: 'warning',
      informational: 'default',
    };

  return (
    <Badge
      variant="filled"
      color={variants[severity]}
      data-testid={`severity-badge-${severity}`}
    >
      {severity}
    </Badge>
  );
};

const transformRuleData = (rule: core_v1_Rule): DetectionRuleTableData => ({
  id: rule.metadata?.name || '',
  name: rule.metadata?.name || '',
  comment: rule.metadata?.comment || '',
  severity: rule.spec.metadata?.severity?.toLowerCase() || 'low',
  rule: new Date(rule.metadata?.modifiedTimestamp || ''),
  version: String(rule.spec.metadata?.version || ''),
  category: rule.spec.metadata?.category || '',
  fidelity: rule.spec.metadata?.fidelity || '',
  mitreTactic: rule.spec.metadata?.mitre?.[0]?.tactic || '',
  mitreTechnique: rule.spec.metadata?.mitre?.[0]?.technique || '',
  mitreTechniqueId: rule.spec.metadata?.mitre?.[0]?.techniqueId || '',
  subTechnique: rule.spec.metadata?.mitre?.[0]?.subTechnique || '',
  subTechniqueId: rule.spec.metadata?.mitre?.[0]?.subTechniqueId || '',
  objective: rule.spec.metadata?.objective || '',
  schedule: rule.spec.schedule?.atLeastEvery || '',
  inputTables: rule.spec.input.stream?.tables?.map((t) => t?.name || '') || [],
  originalRule: rule,
});

const useGetAllListRules = () => {
  const {
    data,
    isLoading: isLoadingRules,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    error,
    refetch,
  } = useGetRulesInfinite();

  useEffect(() => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

  const isLoading = isLoadingRules || isFetchingNextPage || hasNextPage;

  const items = useMemo(
    () =>
      data?.pages.flatMap((page) => page.items.map(transformRuleData)) ?? [],
    [data?.pages],
  );

  return { items, isLoading, error, refetch };
};

function DetectionRulesTableContent() {
  const navigate = useNavigate();
  const [ruleToDelete, setRuleToDelete] = useState<core_v1_Rule | undefined>(
    undefined,
  );

  const { items, isLoading, error, refetch } = useGetAllListRules();
  // Filters on specific columns
  const { filteredItems, setFilter, filters } = useTableColumnFiltering(items);
  // Sorts the items for specific columns
  const { sortedItems, setSortBy, sortKey, sortDirection } =
    useTableColumnSorting(filteredItems);

  // Creates & manages the columns
  const {
    columns,
    columnVisibility,
    columnOrders,
    columnWidths,
    pinnedColumns,
    toggleColumnVisibility,
    resetAllColumns,
    onColumnOrderChange,
  } = useTableColumns({
    sortKey,
    sortDirection,
    setSortBy,
    setFilter,
    filters,
  });

  // Searches all columns
  const { fuzzyFilter, fuzzyFilteredItems, setFuzzyFilter } =
    useTableRowFuzzyFiltering(sortedItems, columnVisibility);

  const { fixedHeaderContent, itemContent } =
    useVirtuosoTableRows<DetectionRuleTableData>({
      columns,
      isLoading,
      customBodyCells: useMemo(
        () => ({
          severity: ({ item }) => <SeverityBadge severity={item.severity} />,
          edit: ({ item }) => (
            <Table.ActionsCell
              data-testid={`actions-cell-${item.id}`}
              actions={[
                {
                  label: 'Delete',
                  icon: 'antimatterhq:trash',
                  onClick: () => {
                    setRuleToDelete(item.originalRule);
                  },
                },
                {
                  label: 'Edit',
                  icon: 'antimatterhq:pencil',
                  onClick: () =>
                    navigate(`/configure/detection-rules/edit/${item.name}`),
                },
              ]}
            />
          ),
        }),
        [navigate],
      ),
      'data-testid': 'detection-rules-table',
    });

  const handleCreateNewRule = useCallback(() => {
    navigate('/configure/detection-rules/add');
  }, [navigate]);

  return (
    <div data-testid="detection-rules-page">
      <Table.VirtualizedPageContainer>
        <div className="flex items-center justify-end gap-2">
          <div className="relative mr-auto flex w-64">
            <Input
              placeholder="Filter all columns"
              value={fuzzyFilter}
              onChange={(e) => setFuzzyFilter(e.target.value)}
              className={cn('mr-1 w-64', !fuzzyFilter && 'mr-auto')}
              data-testid="detection-rules-table-filter-input"
            />
            {fuzzyFilter && (
              <Button
                variant="tertiary"
                className="absolute right-0 top-1/2 -translate-y-1/2"
                onClick={() => setFuzzyFilter('')}
              >
                <Icon icon="lucide:x" />
              </Button>
            )}
          </div>
          <Table.ColumnManager
            pinnedColumns={pinnedColumns}
            columnOrders={columnOrders}
            columnVisibility={columnVisibility}
            toggleColumnVisibility={toggleColumnVisibility}
            resetColumns={resetAllColumns}
            onColumnOrderChange={onColumnOrderChange}
            data-testid="detection-rules-column-manager"
          />
          <Button
            variant="primary"
            onClick={handleCreateNewRule}
            leftIcon="radix-icons:plus"
            data-testid="create-new-rule-button"
          >
            Create New Rule
          </Button>
        </div>

        <Spacer size="lg" />

        <Table.StyleProvider
          virtualized={true}
          pageSize={fuzzyFilteredItems.length}
          columnWidths={columnWidths}
          columnVisibility={columnVisibility}
          data-testid="detection-rules-table-style-provider"
        >
          <Table.Virtual
            columns={columns}
            data={fuzzyFilteredItems}
            isLoading={isLoading}
            itemContent={itemContent}
            fixedHeaderContent={fixedHeaderContent}
            emptyMessage="No rules found"
            error={error}
            refetch={refetch}
          />
        </Table.StyleProvider>
      </Table.VirtualizedPageContainer>
      <DeleteDetectionRulesDialog
        rule={ruleToDelete}
        onClose={() => setRuleToDelete(undefined)}
      />
    </div>
  );
}

export function DetectionRulesTable() {
  return (
    <Table.Provider
      columnDefinitions={detectionRulesTableColumnDefinitions}
      localStorageKeyPrefix="detection-rules-table"
    >
      <DetectionRulesTableContent />
    </Table.Provider>
  );
}
